import React from 'react';

import { NotificationAction } from '@common/typescript/objects/NotificationAction';
import { ItemProviderContext, useItemProviderContext } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { WithDeleted } from '@common/typescript/objects/WithDeleted';
import { Notification } from '@common/typescript/objects/Notification';
import { BaseUser } from '@common/typescript/objects/BaseUser';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import { defaultEqualPredicate, EqualPredicateDelegate } from '@common/react/objects/Core';

export type ItemNotificationActionDelegate<T extends WithDeleted> = (
	notification: Notification<BaseUser>,
	context: ItemProviderContext<T>) => void;

interface ItemProviderSynchronizerProps<T extends WithDeleted> {
	/**
	 * type(s) of notification for filtering before call customHandler
	 */
	objectType: string | Array<string>;
	/**
	 * subtype of notification for filtering before call customHandler
	 */
	objectSubtype?: string;
	/**
	 * equalPredicate to find item at ItemProvider for notification
	 * by default compare item ids
	 * @param a - item from itemProvider
	 * @param b - item from notification
	 * @returns - if equals true, else false
	 */
	equalPredicate?: EqualPredicateDelegate<T>;
	/**
	 * handle update and delete notifications filtered by objectType and objectSubType
	 * using equalPredicate to check item from notification and itemProvider are equals
	 * in case of override excludes the call of onUpdate and onDelete callbacks
	 *
	 * @param notification - notification with data filtered by service flag, objectType and objectSubType
	 * @param context - ItemProviderContext
	 * @param props - CustomHandlerProps
	 */
	customHandler?: (
		notification: Notification<BaseUser>,
		context: ItemProviderContext<T>,
		props: CustomHandlerProps<T>,
	) => void;
	/**
	 * handle any types of notification without filtration
	 * in case of override excludes the call of customHandler, onUpdate and onDelete callbacks
	 *
	 * @param notification - notification with any data
	 * @param context - ItemProviderContext
	 */
	handle?: (notification: Notification<BaseUser>, context: ItemProviderContext<T>) => void;
	/**
	 * onUpdate callback - calls when receive update notification, and customHandler or handle not override
	 *
	 * @param notification - notification with T
	 * @param context - ItemProviderContext
	 */
	onUpdate?: ItemNotificationActionDelegate<T>;
	/**
	 * onDelete callback - calls when receive delete notification, and customHandler or handle not override
	 *
	 * @param notification - notification with T
	 * @param context - ItemProviderContext
	 */
	onDelete?: ItemNotificationActionDelegate<T>;
	/**
	 * handle init notification to setItem at ItemProvider
	 *
	 * @param notification - notification with type 'Init' only
	 * @returns T - item
	 *
	 */
	getItemFromInit?: (notification: Notification<BaseUser>) => T;
}

interface CustomHandlerProps<T extends WithDeleted> {
	equalPredicate: EqualPredicateDelegate<T>;
	onUpdate: ItemNotificationActionDelegate<T>;
	onDelete?: ItemNotificationActionDelegate<T>;
}

const defaultCustomHandler = <T extends WithDeleted>(
	notification: Notification<BaseUser>,
	context: ItemProviderContext<T>,
	{ equalPredicate, onUpdate, onDelete }: CustomHandlerProps<T>,
) => {
	const { state: { item } } = context;
	const { data, action } = notification;

	if (equalPredicate(item, data)) {
		if (action === NotificationAction.Update) {
			onUpdate(notification, context);
		}

		if (action === NotificationAction.Delete) {
			onDelete?.(notification, context);
		}
	}
};

/**
 * ItemProviderSynchronizer component
 * will subscribe on notifications and try to handle it with using outer ItemProvider context
 *
 * @example inside ItemProvider with default handler:
 * <ItemProvider<T> loadRequest="request">
 *   <ItemProviderSynchronizer<T> type="notificationType" />
 * </ItemProvider>
 *
 * @example inside ItemProvider with customHandler, handle all notification with type 'type':
 * <ItemProvider<T> loadRequest="request">
 *   <ItemProviderSynchronizer<T>
 *     type="notificationType"
 *     customHandler={(notification, context) => console.log(notification)}
 *   />
 * </ItemProvider>
 *
 * @example inside ItemProviderWithStore with getItemFromInit to initialize redux data from init object:
 * <ItemProviderWithStore<T> loadRequest="request" storeName="name">
 *   <ItemProviderSynchronizer<T> type="notificationType" getItemFromInit={(notification) => notification.data.item} />
 * </ItemProviderWithStore>
 *
 * @typeParam T - T Any {@link WithDeleted}
 * @param props - {@link ItemProviderSynchronizerProps}
 * @type {React.FC<ItemProviderSynchronizerProps>}
 * @returns React.Fragment
 */
const ItemProviderSynchronizer = <T extends WithDeleted, >(props: ItemProviderSynchronizerProps<T>) => {
	const {
		equalPredicate = defaultEqualPredicate,
		objectType,
		objectSubtype,
		customHandler = defaultCustomHandler,
		onUpdate = ({ data }, { actions: { setItem } }) => (setItem((prev) => ({ ...prev, ...data }))),
		onDelete,
		handle = (notification, context) => {
			if (notification.data && notification.service
				&& ((Array.isArray(objectType) && objectType.includes(notification.objectType))
					|| notification.objectType === objectType)
				&& ((objectSubtype && notification.objectSubtype === objectSubtype)
					|| (!objectSubtype && !notification.objectSubtype))) {
				customHandler(notification, context, { equalPredicate, onUpdate, onDelete });
			}
		},
		getItemFromInit,
	} = props;

	const context = useItemProviderContext<T>();
	const { subscribe } = useApplicationContext();

	const {
		state: { item },
		actions: { setItem },
	} = context;

	const _handle = (notification: Notification<BaseUser>) => {
		if (notification.objectType === 'Init' && getItemFromInit) {
			setItem(getItemFromInit(notification));
		}

		handle(notification, context);
	};

	React.useEffect(subscribe(_handle), [item]);

	return <></>;
};

export default ItemProviderSynchronizer;
